<template>
  <div class="shift-report-unified">
    <b-overlay :show="loading" rounded="sm" opacity="0.6">

    <!-- ===== HEADER ===== -->
    <div class="sru-header no-print">
      <div class="sru-header-left">
        <label class="sru-date-label">Data:</label>
        <input type="date" class="sru-date-input input-pentagro" v-model="referenceDate" @change="onDateChange" />
      </div>
      <div class="sru-header-right">
        <button class="sru-btn-export no-print" @click="abrirModalExcel">
          <b-icon-file-earmark-excel-fill />
          <span>Exportar Excel</span>
        </button>
        <button class="sru-btn-export no-print" @click="exportPDF">
          <b-icon-file-earmark-pdf-fill />
          <span>Exportar PDF</span>
        </button>
        <button class="sru-btn-save no-print" @click="salvarDados" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm mr-1"></span>
          Salvar
        </button>
      </div>
    </div>

    <!-- ===== PRINT TITLE ===== -->
    <div class="print-only print-main-title">
      <h2>Relatório de Turno — {{ referenceDateFormatted }}</h2>
    </div>

    <!-- ===== ABAS ===== -->
    <div class="sru-card no-print-tabs">
      <div>
        <h1 class="sru-card-title">Relatório do Turno</h1>
      </div>
    <div class="sru-tabs">
      <div class="sru-tab-list no-print">
        <div
          v-for="(tab, idx) in tabs"
          :key="idx"
          class="sru-tab-item"
          :class="{ active: activeTab === idx }"
          @click="activeTab = idx"
        >{{ tab.label }}</div>
      </div>

      <!-- ABA: RELATOS -->
      <div v-show="activeTab === 0" class="sru-tab-content">
        <div class="print-only print-tab-title">1. Relatos</div>
        <div class="sru-table-wrapper" tabindex="0" @paste="e => onPaste(e, 'relatos')">
          <table class="sru-table sru-table--relatos">
            <thead>
              <tr>
                <th class="no-print"></th>
                <th>Turno</th>
                <th class="sru-col-area">
                  Área
                  <button class="btn-manage no-print" @click="abrirGerenciador('area', 'Áreas')" title="Gerenciar Áreas">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>
                  Tipo
                  <button class="btn-manage no-print" @click="abrirGerenciador('tipo', 'Tipos')" title="Gerenciar Tipos">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>Descrição</th>
                <th>Data/Hora Inserção</th>
                <th class="no-print"></th>
                <th class="no-print"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in relatos"
                :key="row._key"
                :class="{ 'late-insertion': row._isLate, 'row-readonly': row._fromDb && isPastDate }"
              >
                <td class="no-print sru-linha-num">{{ idx + 1 }}</td>
                <td>
                  <select v-model="row.turnoId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="turnoLabel(row.turnoId)">
                    <option :value="null">— Selecione —</option>
                    <option v-for="t in turnos" :key="t.id" :value="t.id">{{ t.description }}</option>
                  </select>
                </td>
                <td class="sru-col-area">
                  <select v-model="row.areaDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(areaOptions, row.areaDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(areaOptions, row.areaDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td>
                  <select v-model="row.tipoDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(tipoOptions, row.tipoDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(tipoOptions, row.tipoDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td><input type="text" v-model="row.description" :disabled="row._fromDb && isPastDate" class="sru-input" /></td>
                <td><input type="text" :value="row.createdAt" readonly class="sru-input sru-timestamp" /></td>
                <td class="no-print">
                  <button
                    class="btn-pin"
                    :class="{ 'btn-pin--active': row.pinned }"
                    :disabled="isPastDate"
                    @click="row.pinned = row.pinned ? 0 : 1"
                    title="Fixar para datas futuras"
                  >📌</button>
                </td>
                <td class="no-print">
                  <button class="btn-delete" @click="deleteRow('relatos', idx)" :disabled="row._fromDb && isPastDate" title="Remover">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button class="btn-add-row no-print" @click="addRow('relatos')" :disabled="false">+ Linha</button>
        <p class="sru-paste-hint no-print">Para importar dados do Excel, copie as linhas desejadas e pressione Ctrl+V sobre o cabeçalho da tabela.</p>
      </div>

      <!-- ABA: PRODUÇÃO -->
      <div v-show="activeTab === 1" class="sru-tab-content">
        <div class="print-only print-tab-title">2. Indicadores de Performance</div>
        <div class="sru-table-wrapper sru-table-wrapper--producao" tabindex="0" @paste="e => onPaste(e, 'producao')">
          <table class="sru-table sru-table--producao">
            <thead>
              <tr>
                <th class="no-print sru-col-num"></th>
                <th class="sru-col-tipo">
                  Tipo
                  <button class="btn-manage no-print" @click="abrirGerenciador('tipo', 'Tipos')" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th class="sru-col-indicador">
                  Indicador
                  <button class="btn-manage no-print" @click="abrirGerenciador('indicador', 'Indicadores')" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th class="sru-col-turno">{{ turnos[0] ? turnos[0].description : 'Turno A' }}</th>
                <th class="sru-col-turno">{{ turnos[1] ? turnos[1].description : 'Turno B' }}</th>
                <th class="sru-col-turno">{{ turnos[2] ? turnos[2].description : 'Turno C' }}</th>
                <th class="sru-col-total">Total</th>
                <th class="sru-col-meta">Meta</th>
                <th class="sru-col-obs">Observação</th>
                <th class="sru-col-ts">Data/Hora Inserção</th>
                <th class="no-print sru-col-pin"></th>
                <th class="no-print sru-col-del"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in producao"
                :key="row._key"
                :class="{ 'late-insertion': row._isLate, 'row-readonly': row._fromDb && isPastDate }"
              >
                <td class="no-print sru-linha-num">{{ idx + 1 }}</td>
                <td class="sru-col-tipo">
                  <select v-model="row.tipoDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(tipoOptions, row.tipoDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(tipoOptions, row.tipoDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td class="sru-col-indicador">
                  <select v-model="row.indicadorDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(indicadorOptions, row.indicadorDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(indicadorOptions, row.indicadorDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td class="sru-col-turno"><input type="text" inputmode="decimal" v-model="row.turnoA" @input="sanitizeNumericInput(row, 'turnoA', $event); updateTotal(row)" :disabled="row._fromDb && isPastDate" class="sru-input sru-input-num" /></td>
                <td class="sru-col-turno"><input type="text" inputmode="decimal" v-model="row.turnoB" @input="sanitizeNumericInput(row, 'turnoB', $event); updateTotal(row)" :disabled="row._fromDb && isPastDate" class="sru-input sru-input-num" /></td>
                <td class="sru-col-turno"><input type="text" inputmode="decimal" v-model="row.turnoC" @input="sanitizeNumericInput(row, 'turnoC', $event); updateTotal(row)" :disabled="row._fromDb && isPastDate" class="sru-input sru-input-num" /></td>
                <td class="sru-col-total"><input type="text" inputmode="decimal" v-model="row.total" @input="sanitizeNumericInput(row, 'total', $event); row._manualTotal = !!row.total" :disabled="row._fromDb && isPastDate" class="sru-input sru-input-num" /></td>
                <td class="sru-col-meta"><input type="text" inputmode="decimal" v-model="row.meta" @input="sanitizeNumericInput(row, 'meta', $event)" :disabled="row._fromDb && isPastDate" class="sru-input sru-input-num" /></td>
                <td class="sru-col-obs"><input type="text" v-model="row.observacao" :disabled="row._fromDb && isPastDate" class="sru-input" /></td>
                <td class="sru-col-ts"><input type="text" :value="row.createdAt" readonly class="sru-input sru-timestamp" /></td>
                <td class="no-print sru-col-pin">
                  <button
                    class="btn-pin"
                    :class="{ 'btn-pin--active': row.pinned }"
                    :disabled="isPastDate"
                    @click="row.pinned = row.pinned ? 0 : 1"
                    title="Fixar para datas futuras"
                  >📌</button>
                </td>
                <td class="no-print sru-col-del">
                  <button class="btn-delete" @click="deleteRow('producao', idx)" :disabled="row._fromDb && isPastDate" title="Remover">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button class="btn-add-row no-print" @click="addRow('producao')" :disabled="false">+ Linha</button>
        <p class="sru-paste-hint no-print">Para importar dados do Excel, copie as linhas desejadas e pressione Ctrl+V sobre o cabeçalho da tabela.</p>
      </div>

      <!-- ABA: MANUTENÇÃO -->
      <div v-show="activeTab === 2" class="sru-tab-content">
        <div class="print-only print-tab-title">3. Notas de Manutenção</div>
        <div class="sru-table-wrapper" tabindex="0" @paste="e => onPaste(e, 'manutencao')">
          <table class="sru-table">
            <thead>
              <tr>
                <th class="no-print"></th>
                <th>Turno</th>
                <th>Nota/OS</th>
                <th>
                  Prioridade
                  <button class="btn-manage no-print" @click="abrirGerenciador('prioridade', 'Prioridades')" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>
                  Área
                  <button class="btn-manage no-print" @click="abrirGerenciador('area', 'Áreas')" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>
                  Equipamento
                  <button class="btn-manage no-print" @click="abrirGerenciadorEquipamento" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>
                  Oficina
                  <button class="btn-manage no-print" @click="abrirGerenciador('oficina', 'Oficinas')" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>
                  Status
                  <button class="btn-manage no-print" @click="abrirGerenciador('status_manutencao', 'Status')" title="Gerenciar">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#666"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th>Serviço</th>
                <th>Data/Hora Inserção</th>
                <th class="no-print"></th>
                <th class="no-print"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in manutencao"
                :key="row._key"
                :class="{ 'late-insertion': row._isLate, 'row-readonly': row._fromDb && isPastDate }"
              >
                <td class="no-print sru-linha-num">{{ idx + 1 }}</td>
                <td>
                  <select v-model="row.turnoId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="turnoLabel(row.turnoId)">
                    <option :value="null">— Selecione —</option>
                    <option v-for="t in turnos" :key="t.id" :value="t.id">{{ t.description }}</option>
                  </select>
                </td>
                <td><input type="text" v-model="row.description" :disabled="row._fromDb && isPastDate" class="sru-input" /></td>
                <td>
                  <select v-model="row.prioridadeDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(prioridadeOptions, row.prioridadeDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(prioridadeOptions, row.prioridadeDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td>
                  <select v-model="row.areaDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(areaOptions, row.areaDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(areaOptions, row.areaDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td>
                  <select v-model="row.equipamento" :disabled="row._fromDb && isPastDate" class="sru-select" :title="row.equipamento || ''">
                    <option value="">—</option>
                    <option v-for="e in equipmentList" :key="e.id" :value="e.description">{{ e.description }}</option>
                  </select>
                </td>
                <td>
                  <select v-model="row.oficinaDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(domainsByType('oficina'), row.oficinaDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(oficinaOptions, row.oficinaDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td>
                  <select v-model="row.statusDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(statusManutencaoOptions, row.statusDomainId)">
                    <option :value="null">—</option>
                    <option v-for="d in withInactive(statusManutencaoOptions, row.statusDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td><input type="text" v-model="row.otOs" :disabled="row._fromDb && isPastDate" class="sru-input sru-input-sm" /></td>
                <td><input type="text" :value="row.createdAt" readonly class="sru-input sru-timestamp" /></td>
                <td class="no-print">
                  <button
                    class="btn-pin"
                    :class="{ 'btn-pin--active': row.pinned }"
                    :disabled="isPastDate"
                    @click="row.pinned = row.pinned ? 0 : 1"
                    title="Fixar para datas futuras"
                  >📌</button>
                </td>
                <td class="no-print">
                  <button class="btn-delete" @click="deleteRow('manutencao', idx)" :disabled="row._fromDb && isPastDate" title="Remover">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button class="btn-add-row no-print" @click="addRow('manutencao')" :disabled="false">+ Linha</button>
        <p class="sru-paste-hint no-print">Para importar dados do Excel, copie as linhas desejadas e pressione Ctrl+V sobre o cabeçalho da tabela.</p>
      </div>

      <!-- ABA: REDUÇÃO DE MOAGEM -->
      <div v-show="activeTab === 3" class="sru-tab-content">
        <div class="print-only print-tab-title">4. Redução de Moagem</div>
        <div class="sru-table-wrapper sru-table-wrapper--moagem" tabindex="0" @paste="e => onPaste(e, 'moagem')">
          <table class="sru-table sru-table--moagem">
            <thead>
              <tr>
                <th class="no-print moagem-col-num"></th>
                <th class="moagem-col-turno">Turno</th>
                <th class="moagem-col-time">Início</th>
                <th class="moagem-col-time">Término</th>
                <th class="moagem-col-dur">Duração</th>
                <th class="moagem-col-rot">Rotação</th>
                <th class="moagem-col-um" title="Unidade de medida">
                  U.M.
                  <button class="btn-manage no-print" @click="abrirGerenciador('unidade_moagem', 'Unidades de Medida')" title="Gerenciar Unidades de Medida">
                    <svg viewBox="0 0 24 24" style="width:13px;fill:#B0B0B0"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>
                  </button>
                </th>
                <th class="moagem-col-motivo">Motivo</th>
                <th class="moagem-col-ts">Data/Hora Inserção</th>
                <th class="no-print moagem-col-pin"></th>
                <th class="no-print moagem-col-del"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, idx) in moagem"
                :key="row._key"
                :class="{ 'late-insertion': row._isLate, 'row-readonly': row._fromDb && isPastDate }"
              >
                <td class="no-print sru-linha-num moagem-col-num">{{ idx + 1 }}</td>
                <td class="moagem-col-turno">
                  <select v-model="row.turnoId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="turnoLabel(row.turnoId)">
                    <option :value="null">— Selecione —</option>
                    <option v-for="t in turnos" :key="t.id" :value="t.id">{{ t.description }}</option>
                  </select>
                </td>
                <td class="moagem-col-time">
                  <input type="time" v-model="row.inicio" :disabled="row._fromDb && isPastDate" @input="calcDuracao(row)" class="sru-input sru-time" />
                </td>
                <td class="moagem-col-time">
                  <input type="time" v-model="row.termino" :disabled="row._fromDb && isPastDate" @input="calcDuracao(row)" class="sru-input sru-time" />
                </td>
                <td class="moagem-col-dur">
                  <input type="text" :value="row.duracao" readonly class="sru-input sru-duracao" />
                </td>
                <td class="moagem-col-rot"><input type="text" v-model="row.observacao" :disabled="row._fromDb && isPastDate" class="sru-input" /></td>
                <td class="moagem-col-um">
                  <select v-model="row.umDomainId" :disabled="row._fromDb && isPastDate" class="sru-select" :title="domainLabel(umMoagemOptions, row.umDomainId)">
                    <option :value="null">-</option>
                    <option v-for="d in withInactive(umMoagemOptions, row.umDomainId)" :key="d.id" :value="d.id">{{ d.value }}</option>
                  </select>
                </td>
                <td class="moagem-col-motivo"><input type="text" v-model="row.motivo" :disabled="row._fromDb && isPastDate" class="sru-input" /></td>
                <td class="moagem-col-ts"><input type="text" :value="row.createdAt" readonly class="sru-input sru-timestamp" /></td>
                <td class="no-print moagem-col-pin">
                  <button
                    class="btn-pin"
                    :class="{ 'btn-pin--active': row.pinned }"
                    :disabled="isPastDate"
                    @click="row.pinned = row.pinned ? 0 : 1"
                    title="Fixar para datas futuras"
                  >📌</button>
                </td>
                <td class="no-print moagem-col-del">
                  <button class="btn-delete" @click="deleteRow('moagem', idx)" :disabled="row._fromDb && isPastDate" title="Remover">🗑</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button class="btn-add-row no-print" @click="addRow('moagem')" :disabled="false">+ Linha</button>
        <p class="sru-paste-hint no-print">Para importar dados do Excel, copie as linhas desejadas e pressione Ctrl+V sobre o cabeçalho da tabela.</p>
      </div>
    </div>
    </div>

    <!-- ===== FECHAMENTO DO TURNO (seção existente) ===== -->
    <div class="sru-fechamento no-print-fechamento">
      <hr class="sru-divider no-print" />
      <WorkShiftClosing v-if="fechamentoMounted" />
    </div>

    <!-- ===== MODAL: GERENCIADOR DE DOMÍNIOS ===== -->
    <b-modal
      id="modalDomainManager"
      :title="modalDomainTitle"
      hide-footer
      size="md"
      @hidden="onDomainModalHidden"
    >
      <b-overlay :show="savingDomains" rounded="sm" opacity="0.6">
      <!-- Input de pesquisa -->
      <div class="domain-search-row">
        <input
          type="text"
          v-model="domainSearchQuery"
          class="sru-input domain-input"
          placeholder="Pesquisar..."
          autocomplete="off"
        />
      </div>

      <!-- Área rolável: presets + itens editáveis -->
      <div class="domain-scroll-area">

      <!-- Seção de presets recolhível -->
      <template v-if="hasMatchingPresets">
        <div class="group-header" @click="presetsCollapsed = !presetsCollapsed">
          <span>{{ presetGroupLabel }}</span>
          <b-icon-chevron-up v-if="!presetsCollapsed" />
          <b-icon-chevron-down v-if="presetsCollapsed" />
        </div>
        <transition name="accordion">
        <div v-show="!presetsCollapsed">
          <div v-if="modalDomainType === 'area' && areaPresets.length" class="domain-presets-section">
            <div
              v-for="p in areaPresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
          <div v-if="modalDomainType === 'indicador' && indicatorPresets.length" class="domain-presets-section">
            <div
              v-for="p in indicatorPresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
          <div v-if="modalDomainType === 'tipo'" class="domain-presets-section">
            <div
              v-for="p in tipoPresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
          <div v-if="modalDomainType === 'prioridade'" class="domain-presets-section">
            <div
              v-for="p in prioridadePresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
          <div v-if="modalDomainType === 'status_manutencao'" class="domain-presets-section">
            <div
              v-for="p in statusPresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
          <div v-if="modalDomainType === 'unidade_moagem'" class="domain-presets-section">
            <div
              v-for="p in umMoagemPresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
          <div v-if="modalDomainType === 'oficina'" class="domain-presets-section">
            <div
              v-for="p in oficinaPresets"
              :key="p.id"
              v-show="!domainSearchQuery || p.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
              class="domain-list-item domain-list-item--preset"
            >
              <input type="text" :value="p.value" disabled class="sru-input domain-input" />
            </div>
          </div>
        </div>
        </transition>
      </template>

      <!-- Seção de itens personalizados recolhível -->
      <template v-if="hasMatchingCustoms">
        <div class="group-header" @click="customsCollapsed = !customsCollapsed">
          <span>Itens Adicionados (Outros)</span>
          <b-icon-chevron-up v-if="!customsCollapsed" />
          <b-icon-chevron-down v-if="customsCollapsed" />
        </div>
        <transition name="accordion">
        <div v-show="!customsCollapsed" class="domain-list">
          <div
            v-for="(item, idx) in editingDomainItems"
            :key="idx"
            v-show="!domainSearchQuery || item.value.toLowerCase().includes(domainSearchQuery.toLowerCase())"
            class="domain-list-item"
          >
            <input
              type="text"
              v-model="editingDomainItems[idx].value"
              class="sru-input domain-input"
            />
            <button class="btn-delete-domain" @click="deleteDomain(item)" title="Remover opção">
              <svg viewBox="0 0 24 24" style="width:14px;fill:currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
            </button>
          </div>
        </div>
        </transition>
      </template>

      </div><!-- /domain-scroll-area -->
      <div class="domain-add-row">
        <input
          type="text"
          v-model="newDomainValue"
          class="sru-input domain-input"
          placeholder="Novo item..."
          @keyup.enter="addDomainItem"
        />
        <button class="btn-add-domain" @click="addDomainItem">+</button>
      </div>
      <div class="domain-footer">
        <button class="btn-save-domain" @click="saveDomains" :disabled="savingDomains">
          <span v-if="savingDomains" class="spinner-border spinner-border-sm mr-1"></span>
          Salvar
        </button>
        <button class="btn-cancel-domain no-print" @click="$bvModal.hide('modalDomainManager')" :disabled="savingDomains">Cancelar</button>
      </div>
      </b-overlay>
    </b-modal>

    <!-- ===== MODAL: GERENCIADOR DE EQUIPAMENTOS ===== -->
    <b-modal
      id="modalEquipmentManager"
      title="Gerenciar: Equipamentos"
      hide-footer
      size="md"
      @hidden="onEquipmentModalHidden"
    >
      <div class="domain-search-row">
        <input
          type="text"
          v-model="equipmentSearchQuery"
          class="sru-input domain-input"
          placeholder="Pesquisar..."
          autocomplete="off"
        />
      </div>

      <div class="domain-scroll-area">
        <div class="equip-list-header">
          <span>Equipamento</span>
          <span>Processo</span>
          <span></span>
        </div>
        <div class="domain-list">
          <div
            v-for="(item, idx) in editingEquipmentItems"
            :key="idx"
            v-show="!equipmentSearchQuery || item.description.toLowerCase().includes(equipmentSearchQuery.toLowerCase())"
            class="domain-list-item equip-list-row"
          >
            <input
              type="text"
              v-model="editingEquipmentItems[idx].description"
              class="sru-input domain-input"
            />
            <select v-model="editingEquipmentItems[idx].processId" class="sru-select domain-input">
              <option :value="null">— Nenhum —</option>
              <option v-for="p in processes" :key="p.id" :value="p.id">{{ p.description }}</option>
            </select>
            <button class="btn-delete-domain" @click="deleteEquipment(item)" title="Remover equipamento">
              <svg viewBox="0 0 24 24" style="width:14px;fill:currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div class="equip-add-row">
        <input
          type="text"
          v-model="newEquipmentValue"
          class="sru-input domain-input"
          placeholder="Novo equipamento..."
          @keyup.enter="addEquipmentItem"
        />
        <select v-model="newEquipmentProcessId" class="sru-select domain-input">
          <option :value="null">— Nenhum —</option>
          <option v-for="p in processes" :key="p.id" :value="p.id">{{ p.description }}</option>
        </select>
        <button class="btn-add-domain" @click="addEquipmentItem">+</button>
      </div>
      <div class="domain-footer">
        <button class="btn-save-domain" @click="saveEquipments">Salvar</button>
        <button class="btn-cancel-domain no-print" @click="$bvModal.hide('modalEquipmentManager')">Cancelar</button>
      </div>
    </b-modal>

    <!-- ===== MODAL: CAMPOS VAZIOS ===== -->
    <b-modal
      id="modalCamposVazios"
      title="Campos não preenchidos"
      hide-footer
      size="md"
      centered
      @hidden="onCamposVaziosHidden"
    >
      <p class="campos-vazios-intro">Os campos abaixo estão vazios. Deseja salvar mesmo assim?</p>
      <div class="campos-vazios-scroll">
        <table class="campos-vazios-table">
          <thead>
            <tr><th>Aba</th><th>Linha</th><th>Campo</th></tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in camposVazios" :key="i">
              <td>{{ item.aba }}</td>
              <td>{{ item.linha }}</td>
              <td>{{ item.campo }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="sru-paste-hint" style="margin-top: 12px;">Valores colados do Excel que não existem no cadastro não são importados e ficam em branco. Verifique os campos acima antes de salvar.</p>
      <div class="campos-vazios-footer">
        <button class="btn-cancel-domain" @click="desfazerAlteracoes">Desfazer alterações</button>
        <button class="btn-save-domain" @click="confirmarSalvar">Salvar mesmo assim</button>
      </div>
    </b-modal>

    <!-- ===== MODAL: SELETOR DE PERÍODO (EXCEL) ===== -->
    <b-modal
      id="modalExcelPeriod"
      title="Exportar para Excel"
      hide-footer
      size="xl"
      centered
    >
      <!-- ID-13: Input de busca laranja estilizado -->
      <div class="excel-search-bar">
        <div class="excel-search-inner">
          <svg class="excel-icon-calendar" viewBox="0 0 24 24"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>
          <input
            type="text"
            class="excel-search-input"
            :value="excelStartDate && excelEndDate ? `${formatDateBR(excelStartDate)}  →  ${formatDateBR(excelEndDate)}` : 'Selecione o período'"
            readonly
            placeholder="Selecione o período"
          />
        </div>
        <svg class="excel-icon-search" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
      </div>

      <div class="excel-modal-body">
        <!-- ID-12: Atalhos de período (lado esquerdo) -->
        <div class="excel-shortcuts">
          <p class="excel-shortcuts-title">Atalhos</p>
          <a href="#" @click.prevent="setPeriodShortcut(0)" :class="{ active: isShortcutActive(0) }">Hoje</a>
          <a href="#" @click.prevent="setPeriodShortcut(7)" :class="{ active: isShortcutActive(7) }">Últimos 7 dias</a>
          <a href="#" @click.prevent="setPeriodShortcut(15)" :class="{ active: isShortcutActive(15) }">Últimos 15 dias</a>
          <a href="#" @click.prevent="setPeriodShortcut(30)" :class="{ active: isShortcutActive(30) }">Últimos 30 dias</a>
          <a href="#" @click.prevent="setPeriodShortcut('month')" :class="{ active: isShortcutActive('month') }">Este mês</a>
          <a href="#" @click.prevent="setPeriodShortcut('lastMonth')" :class="{ active: isShortcutActive('lastMonth') }">Mês anterior</a>
        </div>

        <!-- ID-11: Dois meses simultâneos -->
        <div class="excel-calendars">
          <div class="excel-calendar">
            <div class="cal-header">
              <button class="cal-nav" @click="prevMonth">‹</button>
              <span>{{ calMonthLabel(calYear, calMonth) }}</span>
              <button class="cal-nav" @click="nextMonth1">›</button>
            </div>
            <div class="cal-grid">
              <div class="cal-dow" v-for="(d, di) in ['D','S','T','Q','Q','S','S']" :key="'dow1-' + di">{{ d }}</div>
              <div
                v-for="(cell, i) in calCells(calYear, calMonth)"
                :key="'a' + i"
                class="cal-day"
                :class="calDayClass(cell)"
                @click="cell.day && selectCalDay(cell)"
              >{{ cell.day || '' }}</div>
            </div>
          </div>

          <div class="excel-calendar">
            <div class="cal-header">
              <button class="cal-nav" @click="prevMonth1">‹</button>
              <span>{{ calMonthLabel(calYear2, calMonth2) }}</span>
              <button class="cal-nav" @click="nextMonth">›</button>
            </div>
            <div class="cal-grid">
              <div class="cal-dow" v-for="(d, di) in ['D','S','T','Q','Q','S','S']" :key="'dow2-' + di">{{ d }}</div>
              <div
                v-for="(cell, i) in calCells(calYear2, calMonth2)"
                :key="'b' + i"
                class="cal-day"
                :class="calDayClass(cell)"
                @click="cell.day && selectCalDay(cell)"
              >{{ cell.day || '' }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="excel-modal-footer">
        <span class="excel-period-label">
          {{ excelStartDate ? formatDateBR(excelStartDate) : '—' }}
          <span v-if="excelStartDate !== excelEndDate"> → {{ excelEndDate ? formatDateBR(excelEndDate) : '—' }}</span>
        </span>
        <button class="sru-btn-export" @click="exportExcel" :disabled="exportingExcel || !excelStartDate">
          <span v-if="exportingExcel" class="spinner-border spinner-border-sm"></span>
          <b-icon-file-earmark-excel-fill v-else />
          <span>Exportar .xls</span>
        </button>
      </div>
    </b-modal>

    </b-overlay>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { urlMainAPI, showError, defaultSuccess } from "@/global";
import WorkShiftClosing from "./WorkShiftClosing.vue";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import pentagroLogo from "@/assets/logo-header.png";
import pentagroIcon from "@/assets/PentagroIcon.png";

let _rowKey = 0;
function newKey() { return ++_rowKey; }

function nowTimestamp() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

export default {
  name: "ShiftReportUnified",
  components: { WorkShiftClosing },

  computed: {
    ...mapState(["user"]),

    unitId() {
      return this.user ? parseInt(this.user.UnitId) : null;
    },

    isPastDate() {
      return this.referenceDate < todayISO();
    },

    // Mês 2 do calendário duplo = mês seguinte ao mês 1
    calMonth2() {
      return (this.calMonth + 1) % 12;
    },

    calYear2() {
      return this.calMonth === 11 ? this.calYear + 1 : this.calYear;
    },

    referenceDateFormatted() {
      if (!this.referenceDate) return "";
      const [y, m, d] = this.referenceDate.split("-");
      return `${d}/${m}/${y}`;
    },

    areaOptions() {
      return [...this.areaPresets, ...this.domainsByType('area')];
    },

    indicadorOptions() {
      return [...this.indicatorPresets, ...this.domainsByType('indicador')];
    },

    tipoOptions() {
      return [...this.tipoPresets, ...this.domainsByType('tipo')];
    },

    prioridadeOptions() {
      return [...this.prioridadePresets, ...this.domainsByType('prioridade')];
    },

    statusManutencaoOptions() {
      return [...this.statusPresets, ...this.domainsByType('status_manutencao')];
    },

    umMoagemOptions() {
      return [...this.umMoagemPresets, ...this.domainsByType('unidade_moagem')];
    },

    oficinaOptions() {
      return [...this.oficinaPresets, ...this.domainsByType('oficina')];
    },

    presetGroupLabel() {
      const labels = {
        area: 'Áreas do Sistema (Padrão)',
        indicador: 'Indicadores do Sistema (Padrão)',
        tipo: 'Tipos Padrão',
        prioridade: 'Prioridades Padrão',
        status_manutencao: 'Status Padrão',
        unidade_moagem: 'Unidades do Sistema (Padrão)',
        oficina: 'Oficinas Padrão',
      };
      return labels[this.modalDomainType] || '';
    },

    hasPresets() {
      return ['area', 'indicador', 'tipo', 'prioridade', 'status_manutencao', 'unidade_moagem', 'oficina'].includes(this.modalDomainType);
    },

    hasMatchingPresets() {
      if (!this.hasPresets) return false;
      if (!this.domainSearchQuery) return true;
      const q = this.domainSearchQuery.toLowerCase();
      const map = {
        area: this.areaPresets,
        indicador: this.indicatorPresets,
        tipo: this.tipoPresets,
        prioridade: this.prioridadePresets,
        status_manutencao: this.statusPresets,
        unidade_moagem: this.umMoagemPresets,
        oficina: this.oficinaPresets,
      };
      return (map[this.modalDomainType] || []).some(p => p.value.toLowerCase().includes(q));
    },

    hasMatchingCustoms() {
      if (!this.editingDomainItems.length) return false;
      if (!this.domainSearchQuery) return true;
      const q = this.domainSearchQuery.toLowerCase();
      return this.editingDomainItems.some(i => i.value.toLowerCase().includes(q));
    },

  },

  data() {
    return {
      referenceDate: todayISO(),
      loadedDate: null,
      pendingDate: null,
      activeTab: 0,
      saving: false,
      exportingExcel: false,
      loading: false,

      tabs: [
        { label: "Relatos" },
        { label: "Indicadores de Performance" },
        { label: "Notas de Manutenção" },
        { label: "Redução de Moagem" },
      ],

      turnos: [],
      domains: [],

      relatos: [],
      producao: [],
      manutencao: [],
      moagem: [],

      deletedRelatos: [],
      deletedProducao: [],
      deletedManutencao: [],
      deletedMoagem: [],

      // Domain manager
      allDomains: [],
      areaPresets: [],
      indicatorPresets: [],
      equipmentList: [],
      processes: [],
      // IDs negativos reservados por faixa para não colidir com IDs gerados pelo banco:
      // -10001–-10005: Tipos | -20001–-20003: Prioridades | -30001–-30003: Status Manutenção
      // -40001–-40002: U.M. Moagem | -50001–-50003: Oficinas | próxima faixa disponível: -60001+
      tipoPresets: [
        { id: -10001, value: "Agrícola" },
        { id: -10002, value: "Processo" },
        { id: -10003, value: "Manutenção" },
        { id: -10004, value: "Qualidade" },
        { id: -10005, value: "Segurança" },
      ],
      prioridadePresets: [
        { id: -20001, value: "Alta" },
        { id: -20002, value: "Média" },
        { id: -20003, value: "Baixa" },
      ],
      statusPresets: [
        { id: -30001, value: "Em andamento" },
        { id: -30002, value: "Pendente" },
        { id: -30003, value: "Concluído" },
      ],
      umMoagemPresets: [
        { id: -40001, value: "m/min" },
        { id: -40002, value: "RPM" },
      ],
      oficinaPresets: [
        { id: -50001, value: "Mecânica" },
        { id: -50002, value: "Instrumentação" },
        { id: -50003, value: "Elétrica" },
        { id: -50004, value: "Caldeiraria" },
        { id: -50005, value: "Preditiva" },
        { id: -50006, value: "Manutenção Autônoma" },
      ],
      modalDomainType: null,
      modalDomainTitle: "",
      editingDomainItems: [],
      deletedDomainIds: [],
      savingDomains: false,
      newDomainValue: "",
      domainSearchQuery: "",
      presetsCollapsed: true,
      customsCollapsed: true,
      editingEquipmentItems: [],
      newEquipmentValue: "",
      newEquipmentProcessId: null,
      equipmentSearchQuery: "",
      camposVazios: [],

      fechamentoMounted: false,

      // Excel export
      excelStartDate: todayISO(),
      excelEndDate: todayISO(),
      calSelectingStart: true, // true = próximo clique é início; false = é fim

      // Calendário duplo — mês 1
      calMonth: new Date().getMonth(),
      calYear: new Date().getFullYear(),

    };
  },

  async mounted() {
    this.loading = true;
    await this.loadTurnos();
    await this.loadDomains();
    await this.loadData();
    this.$nextTick(() => { this.fechamentoMounted = true; });
  },

  methods: {
    // ── Data loading ──────────────────────────────────────────────────────────

    async loadTurnos() {
      try {
        const res = await this.$http.get(`${urlMainAPI}/getworkshift`);
        if (res.data) {
          this.turnos = res.data;
        }
      } catch (e) {
        showError(e);
      }
    },

    async loadDomains() {
      if (!this.unitId) return;
      try {
        const [domainsRes, areasRes, indicatorsRes, equipmentsRes, processesRes] = await Promise.all([
          this.$http.get(`${urlMainAPI}/shiftreport/domains?unitId=${this.unitId}`),
          this.$http.get(`${urlMainAPI}/shiftreport/rootareas?unitId=${this.unitId}`),
          this.$http.get(`${urlMainAPI}/shiftreport/indicators?unitId=${this.unitId}`),
          this.$http.get(`${urlMainAPI}/getequipments`),
          this.$http.get(`${urlMainAPI}/getprocess`)
        ]);
        this.domains = domainsRes.data || [];
        const rawAreas = typeof areasRes.data === 'string' ? JSON.parse(areasRes.data) : (areasRes.data || []);
        this.areaPresets = rawAreas.map(i => ({ id: -(i.id), value: i.indicatorName }));
        const rawIndicators = typeof indicatorsRes.data === 'string' ? JSON.parse(indicatorsRes.data) : (indicatorsRes.data || []);
        this.indicatorPresets = rawIndicators.map(i => ({ id: -(i.id), value: i.indicatorName }));
        const rawEquipments = typeof equipmentsRes.data === 'string' ? JSON.parse(equipmentsRes.data) : (equipmentsRes.data || []);
        this.equipmentList = rawEquipments;
        this.processes = processesRes.data || [];
      } catch (e) {
        showError(e);
      }
    },

    async loadData() {
      if (!this.unitId || !this.referenceDate) return;
      this.loading = true;
      try {
        const res = await this.$http.get(`${urlMainAPI}/shiftreport/${this.unitId}/${this.referenceDate}`);
        const data = res.data || {};

        const isLateRow = r => r.createdAt ? r.createdAt.substring(0, 10) > this.referenceDate : false;
        this.relatos = (data.relatos || []).map(r => ({ ...r, _key: newKey(), _fromDb: r.id > 0, _isTemplate: r.id === 0, _isLate: isLateRow(r) }));
        this.producao = (data.producao || []).map(r => ({ ...r, _key: newKey(), _fromDb: r.id > 0, _isTemplate: r.id === 0, _isLate: isLateRow(r), _manualTotal: !!r.total }));
        this.manutencao = (data.manutencao || []).map(r => ({ ...r, _key: newKey(), _fromDb: r.id > 0, _isTemplate: r.id === 0, _isLate: isLateRow(r) }));
        this.moagem = (data.moagem || []).map(r => ({ ...r, _key: newKey(), _fromDb: r.id > 0, _isTemplate: r.id === 0, _isLate: isLateRow(r) }));
        this.allDomains = data.allDomains || [];

        this.deletedRelatos = [];
        this.deletedProducao = [];
        this.deletedManutencao = [];
        this.deletedMoagem = [];
        this.loadedDate = this.referenceDate;
      } catch (e) {
        showError(e);
      } finally {
        this.loading = false;
      }
    },

    async onDateChange() {
      const newDate = this.referenceDate;
      const oldDate = this.loadedDate || newDate;

      // Saindo de um dia retroativo (somente leitura): nao valida/salva, apenas carrega o novo dia
      if (oldDate < todayISO()) {
        await this.loadData();
        return;
      }

      this.descartarLinhasPinnadasVazias();
      const erros = this.validarDados();
      if (erros.length > 0) {
        this.pendingDate = newDate;
        this.referenceDate = oldDate;
        this.camposVazios = erros;
        this.$bvModal.show('modalCamposVazios');
        return;
      }
      const hasUnsaved = [...this.relatos, ...this.producao, ...this.manutencao, ...this.moagem].some(r => r.id === 0) ||
        this.deletedRelatos.length > 0 || this.deletedProducao.length > 0 ||
        this.deletedManutencao.length > 0 || this.deletedMoagem.length > 0;
      await this.executarSalvar({ silent: true });
      if (hasUnsaved) defaultSuccess("Alterações salvas automaticamente.");
      await this.loadData();
    },

    // ── Row management ────────────────────────────────────────────────────────

    addRow(table) {
      const ts = nowTimestamp();
      const isLate = this.isPastDate;

      const currentUserName = this.user ? this.user.FullName : '';
      const baseRow = { _key: newKey(), _fromDb: false, _isLate: isLate, createdAt: ts, unitId: this.unitId, referenceDate: this.referenceDate, userName: currentUserName };

      if (table === "relatos") {
        this.relatos.push({ ...baseRow, turnoId: null, areaDomainId: null, tipoDomainId: null, description: "", pinned: 0 });
      } else if (table === "producao") {
        this.producao.push({ ...baseRow, tipoDomainId: null, indicadorDomainId: null, turnoA: null, turnoB: null, turnoC: null, total: null, meta: null, observacao: "", pinned: 0 });
      } else if (table === "manutencao") {
        this.manutencao.push({ ...baseRow, turnoId: null, equipamento: "", prioridadeDomainId: null, areaDomainId: null, description: "", oficinaDomainId: null, statusDomainId: null, otOs: "", pinned: 0 });
      } else if (table === "moagem") {
        const lastWithUm = [...this.moagem].reverse().find(m => m.umDomainId != null);
        const lastUm = lastWithUm ? lastWithUm.umDomainId : null;
        this.moagem.push({ ...baseRow, turnoId: null, umDomainId: lastUm, inicio: "", termino: "", duracao: "", motivo: "", observacao: "", pinned: 0 });
      }
    },

    deleteRow(table, idx) {
      const row = this[table][idx];
      if (row.id) {
        const deletedKey = `deleted${table.charAt(0).toUpperCase() + table.slice(1)}`;
        this[deletedKey].push(row.id);
      }
      this[table].splice(idx, 1);
    },

    // ── Paste from Excel (todas as abas) ──────────────────────────────────────

    onPaste(event, tabName) {
      const tag = event.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      event.preventDefault();

      const text = (event.clipboardData || window.clipboardData).getData('text');
      if (!text) return;

      const lines = text.split('\n').map(l => l.replace(/\r$/, '')).filter(l => l.trim());
      if (!lines.length) return;

      const matchDomain = (list, txt) => {
        if (!txt) return null;
        const t = txt.trim().toLowerCase();
        const found = list.find(d => String(d.value || '').toLowerCase() === t);
        return found ? found.id : null;
      };
      const matchTurno = (txt) => {
        if (!txt) return null;
        const found = this.turnos.find(x => x.description.toLowerCase() === txt.trim().toLowerCase());
        return found ? found.id : null;
      };
      const cleanNum = (txt) => {
        if (!txt) return null;
        let s = String(txt).replace(/,/g, '.').replace(/[^\d.-]/g, '');
        const firstDot = s.indexOf('.');
        if (firstDot !== -1) s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
        if (s.includes('-')) s = (s.startsWith('-') ? '-' : '') + s.replace(/-/g, '');
        return s || null;
      };

      const ts = nowTimestamp();
      const currentUserName = this.user ? this.user.FullName : '';
      const baseRow = () => ({ _key: newKey(), _fromDb: false, _isLate: this.isPastDate, createdAt: ts, unitId: this.unitId, referenceDate: this.referenceDate, userName: currentUserName });
      const c = (cols, i) => (cols[i] || '').trim();

      let count = 0;

      for (const line of lines) {
        const cols = line.split('\t');

        if (tabName === 'relatos') {
          // Turno | Área | Tipo | Descrição
          this.relatos.push({
            ...baseRow(),
            turnoId: matchTurno(c(cols, 0)),
            areaDomainId: matchDomain(this.areaOptions, c(cols, 1)),
            tipoDomainId: matchDomain(this.tipoOptions, c(cols, 2)),
            description: c(cols, 3),
          });
        } else if (tabName === 'producao') {
          // Tipo | Indicador | Turno A | Turno B | Turno C | Total | Meta | Observação
          const totalCell = cleanNum(c(cols, 5));
          this.producao.push({
            ...baseRow(),
            tipoDomainId: matchDomain(this.tipoOptions, c(cols, 0)),
            indicadorDomainId: matchDomain(this.indicadorOptions, c(cols, 1)),
            turnoA: cleanNum(c(cols, 2)),
            turnoB: cleanNum(c(cols, 3)),
            turnoC: cleanNum(c(cols, 4)),
            total: totalCell,
            _manualTotal: !!totalCell,
            meta: cleanNum(c(cols, 6)),
            observacao: c(cols, 7),
            pinned: 0,
          });
        } else if (tabName === 'manutencao') {
          // Turno | Nota/OS | Prioridade | Área | Equipamento | Oficina | Status | Serviço
          const equipTxt = c(cols, 4);
          const equipMatch = this.equipmentList.find(e => e.description.toLowerCase() === equipTxt.toLowerCase());
          this.manutencao.push({
            ...baseRow(),
            turnoId: matchTurno(c(cols, 0)),
            description: c(cols, 1),
            prioridadeDomainId: matchDomain(this.prioridadeOptions, c(cols, 2)),
            areaDomainId: matchDomain(this.areaOptions, c(cols, 3)),
            equipamento: equipMatch ? equipMatch.description : equipTxt,
            oficinaDomainId: matchDomain(this.oficinaOptions, c(cols, 5)),
            statusDomainId: matchDomain(this.statusManutencaoOptions, c(cols, 6)),
            otOs: c(cols, 7),
          });
        } else if (tabName === 'moagem') {
          // Turno | Início | Término | (Duração - recalculada) | Rotação | U.M. | Motivo
          const row = {
            ...baseRow(),
            turnoId: matchTurno(c(cols, 0)),
            inicio: c(cols, 1),
            termino: c(cols, 2),
            duracao: '',
            observacao: c(cols, 4),
            umDomainId: matchDomain(this.umMoagemOptions, c(cols, 5)),
            motivo: c(cols, 6),
            pinned: 0,
          };
          this.calcDuracao(row);
          this.moagem.push(row);
        }
        count++;
      }

      if (count > 0) {
        defaultSuccess(`${count} linha${count > 1 ? 's importadas' : ' importada'} do Excel.`);
      }
    },

    // ── Producao helpers ──────────────────────────────────────────────────────

    sanitizeNumericInput(row, field, event) {
      const raw = event && event.target ? event.target.value : row[field];
      if (raw === null || raw === undefined || raw === '') {
        if (row[field] !== raw) row[field] = raw;
        return;
      }
      let clean = String(raw).replace(/,/g, '.').replace(/[^\d.-]/g, '');
      const firstDot = clean.indexOf('.');
      if (firstDot !== -1) {
        clean = clean.slice(0, firstDot + 1) + clean.slice(firstDot + 1).replace(/\./g, '');
      }
      if (clean.includes('-')) {
        const neg = clean.startsWith('-');
        clean = (neg ? '-' : '') + clean.replace(/-/g, '');
      }
      if (clean !== raw) {
        row[field] = clean;
        if (event && event.target) event.target.value = clean;
      } else if (row[field] !== clean) {
        row[field] = clean;
      }
    },

    parseNum(v) {
      if (v === null || v === undefined || v === '') return 0;
      const n = parseFloat(String(v).replace(',', '.'));
      return Number.isFinite(n) ? n : 0;
    },

    updateTotal(row) {
      if (row._manualTotal) return;
      const sum = this.parseNum(row.turnoA) + this.parseNum(row.turnoB) + this.parseNum(row.turnoC);
      row.total = sum === 0 ? null : String(sum);
    },

    // ── Moagem duration ───────────────────────────────────────────────────────

    calcDuracao(row) {
      if (!row.inicio || !row.termino) { row.duracao = ""; return; }
      const [h1, m1] = row.inicio.split(":").map(Number);
      const [h2, m2] = row.termino.split(":").map(Number);
      let t1 = h1 * 60 + m1;
      let t2 = h2 * 60 + m2;
      if (t2 < t1) t2 += 1440;
      const diff = t2 - t1;
      row.duracao = `${String(Math.floor(diff / 60)).padStart(2, "0")}:${String(diff % 60).padStart(2, "0")}`;
    },

    openTimePicker(event) {
      const input = event.currentTarget.previousElementSibling;
      if (input && input.showPicker) {
        try { input.showPicker(); } catch (e) { /* ignore */ }
      }
    },

    // ── Domains ───────────────────────────────────────────────────────────────

    domainsByType(type) {
      return this.domains.filter(d => d.domainType === type && d.active === 1);
    },

    turnoLabel(id) {
      if (!id) return '';
      const t = this.turnos.find(x => x.id === id);
      return t ? t.description : '';
    },

    domainLabel(options, id) {
      if (id === null || id === undefined) return '';
      const found = this.withInactive(options, id).find(o => o.id === id);
      return found ? found.value : '';
    },

    abrirGerenciador(type, label) {
      this.modalDomainType = type;
      this.modalDomainTitle = `Gerenciar: ${label}`;
      this.editingDomainItems = this.domainsByType(type).map(d => ({ ...d }));
      this.deletedDomainIds = [];
      this.newDomainValue = "";
      this.domainSearchQuery = "";
      this.presetsCollapsed = true;
      this.customsCollapsed = true;
      this.$bvModal.show("modalDomainManager");
    },

    addDomainItem() {
      const v = (this.newDomainValue || "").trim();
      if (!v) return;
      this.editingDomainItems.push({
        id: 0,
        unitId: this.unitId,
        domainType: this.modalDomainType,
        value: v,
        active: 1,
        createdAt: null
      });
      this.newDomainValue = "";
    },

    async saveDomains() {
      this.savingDomains = true;
      try {
        this.editingDomainItems = this.editingDomainItems.map(item => ({
          ...item,
          value: String(item.value || '').trim()
        }));

        for (const id of this.deletedDomainIds) {
          await this.$http.post(`${urlMainAPI}/shiftreport/domains/delete/${id}`);
        }

        await this.$http.post(
          `${urlMainAPI}/shiftreport/domains/save`,
          this.editingDomainItems
        );

        await this.loadDomains();
        this.$bvModal.hide("modalDomainManager");
        defaultSuccess("Domínios salvos com sucesso!");
      } catch (e) {
        showError(e);
      } finally {
        this.savingDomains = false;
      }
    },

    onDomainModalHidden() {
      this.editingDomainItems = [];
      this.deletedDomainIds = [];
      this.newDomainValue = "";
      this.modalDomainType = null;
    },

    // ── Equipment Manager ─────────────────────────────────────────────────────

    abrirGerenciadorEquipamento() {
      this.editingEquipmentItems = this.equipmentList.map(e => ({ ...e }));
      this.newEquipmentValue = "";
      this.newEquipmentProcessId = null;
      this.equipmentSearchQuery = "";
      this.$bvModal.show("modalEquipmentManager");
    },

    addEquipmentItem() {
      const v = (this.newEquipmentValue || "").trim();
      if (!v) return;
      this.editingEquipmentItems.push({
        id: 0,
        description: v,
        processId: this.newEquipmentProcessId
      });
      this.newEquipmentValue = "";
      this.newEquipmentProcessId = null;
    },

    async saveEquipments() {
      try {
        this.editingEquipmentItems = this.editingEquipmentItems.map(item => ({
          ...item,
          description: String(item.description || '').trim()
        }));

        await this.$http.post(`${urlMainAPI}/saveequipments`, this.editingEquipmentItems);
        const res = await this.$http.get(`${urlMainAPI}/getequipments`);
        const raw = typeof res.data === 'string' ? JSON.parse(res.data) : (res.data || []);
        this.equipmentList = raw;
        this.$bvModal.hide("modalEquipmentManager");
        defaultSuccess("Equipamentos salvos com sucesso!");
      } catch (e) {
        showError(e);
      }
    },

    async deleteEquipment(item) {
      try {
        if (item.id === 0) {
          this.editingEquipmentItems = this.editingEquipmentItems.filter(e => e !== item);
          return;
        }
        await this.$http.post(`${urlMainAPI}/deleteequipments`, [item]);
        const res = await this.$http.get(`${urlMainAPI}/getequipments`);
        const raw = typeof res.data === 'string' ? JSON.parse(res.data) : (res.data || []);
        this.equipmentList = raw;
        this.editingEquipmentItems = raw.map(e => ({ ...e }));
      } catch (e) {
        showError(e);
      }
    },

    onEquipmentModalHidden() {
      this.editingEquipmentItems = [];
      this.newEquipmentValue = "";
      this.newEquipmentProcessId = null;
      this.equipmentSearchQuery = "";
    },

    deleteDomain(item) {
      const idx = this.editingDomainItems.indexOf(item);
      if (idx !== -1) {
        if (item.id) this.deletedDomainIds.push(item.id);
        this.editingDomainItems.splice(idx, 1);
      }
    },

    // Retorna as opções base acrescidas do domínio inativo caso a linha já tenha esse valor salvo
    withInactive(options, currentId) {
      if (!currentId || currentId < 0) return options;
      if (options.find(o => o.id === currentId)) return options;
      const inactive = this.allDomains.find(d => d.id === currentId);
      if (inactive) return [...options, { id: inactive.id, value: `${inactive.value} (removido)` }];
      return options;
    },

    // ── Validation ────────────────────────────────────────────────────────────

    validarDados() {
      const erros = [];

      // Campos preenchidos pelo usuário em cada aba (além da estrutura herdada do pino).
      // Um template sem nenhum desses campos é placeholder intocado ou marcador de despino: não é validado.
      const hasUserData = (row, keys) => keys.some(k => row[k] !== null && row[k] !== undefined && row[k] !== '');
      const isUntouchedTemplate = (row, keys) => row._isTemplate && !hasUserData(row, keys);

      const checks = [
        {
          aba: "Relatos",
          rows: this.relatos,
          userKeys: ["description"],
          campos: [
            { key: "turnoId",      label: "Turno" },
            { key: "areaDomainId", label: "Área" },
            { key: "tipoDomainId", label: "Tipo" },
            { key: "description",  label: "Descrição" },
          ],
        },
        {
          aba: "Indicadores de Performance",
          rows: this.producao,
          userKeys: ["turnoA", "turnoB", "turnoC", "total", "meta", "observacao"],
          campos: [
            { key: "tipoDomainId",      label: "Tipo" },
            { key: "indicadorDomainId", label: "Indicador" },
          ],
        },
        {
          aba: "Notas de Manutenção",
          rows: this.manutencao,
          userKeys: ["description", "otOs"],
          campos: [
            { key: "turnoId",            label: "Turno" },
            { key: "description",        label: "Nota/OS" },
            { key: "prioridadeDomainId", label: "Prioridade" },
            { key: "areaDomainId",       label: "Área" },
            { key: "equipamento",        label: "Equipamento" },
            { key: "oficinaDomainId",    label: "Oficina" },
            { key: "statusDomainId",     label: "Status" },
            { key: "otOs",               label: "Serviço" },
          ],
        },
        {
          aba: "Redução de Moagem",
          rows: this.moagem,
          userKeys: ["inicio", "termino", "motivo", "observacao"],
          campos: [
            { key: "turnoId",   label: "Turno" },
            { key: "inicio",    label: "Início" },
            { key: "termino",   label: "Término" },
            { key: "observacao", label: "Rotação" },
            { key: "motivo",    label: "Motivo" },
          ],
        },
      ];

      for (const { aba, rows, campos, userKeys } of checks) {
        rows.forEach((row, idx) => {
          if (isUntouchedTemplate(row, userKeys)) return;
          for (const { key, label } of campos) {
            const val = row[key];
            const vazio = val === null || val === undefined || val === "";
            if (vazio) erros.push({ aba, linha: idx + 1, campo: label });
          }
        });
      }

      const producaoUserKeys = ["turnoA", "turnoB", "turnoC", "total", "meta", "observacao"];
      this.producao.forEach((row, idx) => {
        if (isUntouchedTemplate(row, producaoUserKeys)) return;
        const hasValue = hasUserData(row, producaoUserKeys);
        if (!hasValue) erros.push({ aba: "Indicadores de Performance", linha: idx + 1, campo: "Valor (Turno / Meta / Observação)" });
      });

      return erros;
    },

    // ── Save ──────────────────────────────────────────────────────────────────

    async salvarDados() {
      if (!this.unitId) return;

      const erros = this.validarDados();
      if (erros.length > 0) {
        this.camposVazios = erros;
        this.$bvModal.show('modalCamposVazios');
        return;
      }

      await this.executarSalvar();
    },

    async confirmarSalvar() {
      const pending = this.pendingDate;
      this.pendingDate = null;
      this.$bvModal.hide('modalCamposVazios');
      this.descartarLinhasPinnadasVazias();
      if (pending) {
        await this.executarSalvar({ silent: true });
        this.referenceDate = pending;
        await this.loadData();
        defaultSuccess("Dados salvos com sucesso!");
      } else {
        await this.executarSalvar();
      }
    },

    async desfazerAlteracoes() {
      const pending = this.pendingDate;
      this.pendingDate = null;
      this.$bvModal.hide('modalCamposVazios');
      if (pending) this.referenceDate = pending;
      await this.loadData();
    },

    onCamposVaziosHidden() {
      // Se o modal foi fechado sem ação (X ou Esc), descarta a troca de data pendente:
      // a referenceDate já foi revertida para a loadedDate quando o modal abriu.
      this.pendingDate = null;
    },

    descartarLinhasPinnadasVazias() {
      // Templates propagados (id 0) nascem com pinned = 1. Regras de descarte:
      //  - com valor digitado pelo usuário  → mantém (vira o novo registro do dia);
      //  - vazio e ainda fixado (pinned = 1) → descarta (placeholder intocado; a fonte continua sendo o último dia com registro);
      //  - vazio e desafixado (pinned = 0)   → mantém como marcador, para gravar o "despino" no dia e interromper a propagação futura.
      const isTemplate = (row) => row._isTemplate;
      const hasValue = (...vals) => vals.some(v => v !== null && v !== undefined && v !== '');

      this.relatos = this.relatos.filter(r =>
        !isTemplate(r) || hasValue(r.description) || r.pinned === 0
      );
      this.producao = this.producao.filter(r =>
        !isTemplate(r) || hasValue(r.turnoA, r.turnoB, r.turnoC, r.total, r.meta, r.observacao) || r.pinned === 0
      );
      this.manutencao = this.manutencao.filter(r =>
        !isTemplate(r) || hasValue(r.description, r.otOs) || r.pinned === 0
      );
      this.moagem = this.moagem.filter(r =>
        !isTemplate(r) || hasValue(r.inicio, r.termino, r.motivo, r.observacao) || r.pinned === 0
      );
    },

    async executarSalvar({ silent = false } = {}) {
      this.saving = true;
      try {
        // Garante que placeholders de pino intocados nunca sejam persistidos como linhas vazias,
        // independentemente do caminho de entrada (Salvar manual, troca de data ou "Salvar mesmo assim").
        this.descartarLinhasPinnadasVazias();
        const payload = {
          relatos: this.relatos.map(r => this.stripMeta(r)),
          producao: this.producao.map(r => this.stripMeta(r)),
          manutencao: this.manutencao.map(r => this.stripMeta(r)),
          moagem: this.moagem.map(r => this.stripMeta(r)),
          deletedRelatos: this.deletedRelatos,
          deletedProducao: this.deletedProducao,
          deletedManutencao: this.deletedManutencao,
          deletedMoagem: this.deletedMoagem,
        };
        const res = await this.$http.post(`${urlMainAPI}/shiftreport/save`, payload);
        const { newRelatoIds = [], newProducaoIds = [], newManutencaoIds = [], newMoagemIds = [] } = res.data;
        this.applyInsertedIds(this.relatos, newRelatoIds);
        this.applyInsertedIds(this.producao, newProducaoIds);
        this.applyInsertedIds(this.manutencao, newManutencaoIds);
        this.applyInsertedIds(this.moagem, newMoagemIds);
        if (!silent) {
          await this.loadData();
          defaultSuccess("Dados salvos com sucesso!");
        }
      } catch (e) {
        showError(e);
      } finally {
        this.saving = false;
      }
    },

    applyInsertedIds(rows, newIds) {
      let i = 0;
      for (const r of rows) {
        if (r.id === 0) r.id = newIds[i++];
      }
    },

    stripMeta(row) {
      // eslint-disable-next-line no-unused-vars
      const { _key, _fromDb, _isTemplate, _isLate, _manualTotal, ...clean } = row;
      return clean;
    },

    // ── PDF export ────────────────────────────────────────────────────────────

    exportPDF() {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      const getDomainValue = (id) => {
        if (!id) return "";
        if (id < 0) {
          const presets = [...this.areaPresets, ...this.indicatorPresets, ...this.tipoPresets, ...this.prioridadePresets, ...this.statusPresets, ...this.umMoagemPresets, ...this.oficinaPresets];
          const p = presets.find(p => p.id === id);
          return p ? p.value : "";
        }
        const d = this.allDomains.find(x => x.id === id);
        return d ? d.value : "";
      };

      const getTurnoDesc = (id) => {
        const t = this.turnos.find(x => x.id === id);
        return t ? t.description : "";
      };

      const tableStyles = {
        headStyles: { fillColor: false, textColor: [0, 0, 0], fontSize: 8, fontStyle: "bold", lineColor: [0, 0, 0], lineWidth: 0.2 },
        bodyStyles: { fontSize: 8, lineColor: [180, 180, 180], lineWidth: 0.1 },
        alternateRowStyles: {},
        showHead: "everyPage",
        margin: { left: 14, right: 14, top: 22 },
        didDrawPage: () => {
          const pw = doc.internal.pageSize.width;
          const ph = doc.internal.pageSize.height;
          const absolutePage = doc.internal.getCurrentPageInfo().pageNumber;
          if (absolutePage === 1) {
            doc.addImage(pentagroLogo, "PNG", 14, 5, 36, 10);
            doc.setDrawColor(200, 200, 200);
            doc.line(14, 18, pw - 14, 18);
          }
          doc.addImage(pentagroIcon, "PNG", pw - 22, 5, 10, 10);
          doc.setFontSize(8);
          doc.setTextColor(120);
          doc.text(`Página ${doc.internal.getNumberOfPages()}`, pw - 20, ph - 8);
        }
      };

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(`Relatório do Turno — ${this.referenceDateFormatted}`, 14, 27);

      const section = (title, startY) => {
        doc.setFontSize(11);
        doc.setTextColor(50, 50, 50);
        doc.text(title, 14, startY);
        return startY + 4;
      };

      // 1. Relatos
      let y = section("1. Relatos", 35);
      autoTable(doc, {
        startY: y,
        head: [["Turno", "Área", "Tipo", "Descrição", "Data/Hora Inserção"]],
        body: this.relatos.map(r => [
          getTurnoDesc(r.turnoId), getDomainValue(r.areaDomainId), getDomainValue(r.tipoDomainId),
          r.description || "", r.createdAt || ""
        ]),
        ...tableStyles,
      });

      // 2. Produção
      y = section("2. Indicadores de Performance", doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: y,
        head: [[
          "Tipo", "Indicador",
          this.turnos[0] ? this.turnos[0].description : "Turno A",
          this.turnos[1] ? this.turnos[1].description : "Turno B",
          this.turnos[2] ? this.turnos[2].description : "Turno C",
          "Total", "Meta", "Observação", "Data/Hora Inserção"
        ]],
        body: this.producao.map(r => [
          getDomainValue(r.tipoDomainId), getDomainValue(r.indicadorDomainId),
          r.turnoA || "", r.turnoB || "", r.turnoC || "", r.total || "", r.meta || "", r.observacao || "", r.createdAt || ""
        ]),
        ...tableStyles,
      });

      // 3. Notas de Manutenção
      y = section("3. Notas de Manutenção", doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: y,
        head: [["Turno", "Nota/OS", "Prioridade", "Área", "Equipamento", "Oficina", "Status", "Serviço", "Data/Hora Inserção"]],
        body: this.manutencao.map(r => [
          getTurnoDesc(r.turnoId), r.description || "", getDomainValue(r.prioridadeDomainId),
          getDomainValue(r.areaDomainId), r.equipamento || "", getDomainValue(r.oficinaDomainId),
          getDomainValue(r.statusDomainId), r.otOs || "", r.createdAt || ""
        ]),
        ...tableStyles,
      });

      // 4. Redução de Moagem
      y = section("4. Redução de Moagem", doc.lastAutoTable.finalY + 10);
      autoTable(doc, {
        startY: y,
        head: [["Turno", "Início", "Término", "Duração", "Rotação", "U.M.", "Motivo", "Data/Hora Inserção"]],
        body: this.moagem.map(r => [
          getTurnoDesc(r.turnoId), r.inicio || "", r.termino || "",
          r.duracao || "", r.observacao || "", getDomainValue(r.umDomainId) || "-", r.motivo || "", r.createdAt || ""
        ]),
        columnStyles: { 6: { cellWidth: "auto" } },
        ...tableStyles,
      });

      doc.save(`relatorio_turno_${this.referenceDate}.pdf`);
    },

    // ── Excel export ──────────────────────────────────────────────────────────

    abrirModalExcel() {
      this.excelStartDate = todayISO();
      this.excelEndDate = todayISO();
      this.calSelectingStart = true;
      // Posiciona calendário no mês atual
      const now = new Date();
      this.calMonth = now.getMonth();
      this.calYear = now.getFullYear();
      this.$bvModal.show("modalExcelPeriod");
    },

    setPeriodShortcut(days) {
      const today = new Date();
      this.excelEndDate = todayISO();
      if (days === 0) {
        this.excelStartDate = todayISO();
      } else if (days === "month") {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        this.excelStartDate = d.toISOString().split("T")[0];
      } else if (days === "lastMonth") {
        const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        this.excelStartDate = d.toISOString().split("T")[0];
        this.excelEndDate = end.toISOString().split("T")[0];
      } else {
        const d = new Date();
        d.setDate(d.getDate() - days);
        this.excelStartDate = d.toISOString().split("T")[0];
      }
    },

    async exportExcel() {
      if (this.excelStartDate > this.excelEndDate) {
        showError("A data de início deve ser anterior ou igual à data fim.");
        return;
      }
      this.exportingExcel = true;
      try {
        const res = await this.$http.get(
          `${urlMainAPI}/shiftreport/export?unitId=${this.unitId}&startDate=${this.excelStartDate}&endDate=${this.excelEndDate}`
        );
        const data = res.data;
        this.downloadExcel(data);
        this.$bvModal.hide("modalExcelPeriod");
      } catch (e) {
        showError(e);
      } finally {
        this.exportingExcel = false;
      }
    },

    downloadExcel(data) {
      const getDomainValue = (id) => {
        if (!id) return "";
        if (id < 0) {
          const a = this.areaPresets.find(p => p.id === id);
          if (a) return a.value;
          const i = this.indicatorPresets.find(p => p.id === id);
          if (i) return i.value;
          const t = this.tipoPresets.find(p => p.id === id);
          if (t) return t.value;
          const pr = this.prioridadePresets.find(p => p.id === id);
          if (pr) return pr.value;
          const um = this.umMoagemPresets.find(p => p.id === id);
          if (um) return um.value;
          const st = this.statusPresets.find(p => p.id === id);
          if (st) return st.value;
          const of = this.oficinaPresets.find(p => p.id === id);
          if (of) return of.value;
          return "";
        }
        const d = (data.domains || []).find(x => x.id === id);
        return d ? d.value : "";
      };
      const getTurnoDesc = (id) => {
        const t = this.turnos.find(x => x.id === id);
        return t ? t.description : "";
      };

      const escapeXml = (v) => String(v || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

      const makeSheet = (name, headers, rows) => {
        const hRow = headers.map(h => `<Cell><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`).join("");
        const dataRows = rows.map(cols => {
          const cells = cols.map(c => `<Cell><Data ss:Type="String">${escapeXml(c)}</Data></Cell>`).join("");
          return `<Row>${cells}</Row>`;
        }).join("");
        return `<Worksheet ss:Name="${escapeXml(name)}"><Table><Row>${hRow}</Row>${dataRows}</Table></Worksheet>`;
      };

      const relatosSheet = makeSheet("Relatos",
        ["Turno", "Área", "Tipo", "Descrição", "Data/Hora Inserção"],
        (data.relatos || []).map(r => [getTurnoDesc(r.turnoId), getDomainValue(r.areaDomainId), getDomainValue(r.tipoDomainId), r.description, r.createdAt])
      );

      const turnoALabel = this.turnos[0] ? this.turnos[0].description : 'Turno A';
      const turnoBLabel = this.turnos[1] ? this.turnos[1].description : 'Turno B';
      const turnoCLabel = this.turnos[2] ? this.turnos[2].description : 'Turno C';
      const producaoSheet = makeSheet("Indicadores de Performance",
        ["Tipo", "Indicador", turnoALabel, turnoBLabel, turnoCLabel, "Total", "Meta", "Observação", "Data/Hora Inserção"],
        (data.producao || []).map(r => [getDomainValue(r.tipoDomainId), getDomainValue(r.indicadorDomainId), r.turnoA, r.turnoB, r.turnoC, r.total, r.meta, r.observacao, r.createdAt])
      );

      const manutencaoSheet = makeSheet("Notas de Manutenção",
        ["Turno", "Nota/OS", "Prioridade", "Área", "Equipamento", "Oficina", "Status", "Serviço", "Data/Hora Inserção"],
        (data.manutencao || []).map(r => [getTurnoDesc(r.turnoId), r.description, getDomainValue(r.prioridadeDomainId), getDomainValue(r.areaDomainId), r.equipamento, getDomainValue(r.oficinaDomainId), getDomainValue(r.statusDomainId), r.otOs, r.createdAt])
      );

      const moagemSheet = makeSheet("Redução de Moagem",
        ["Turno", "Início", "Término", "Duração", "Rotação", "U.M.", "Motivo", "Data/Hora Inserção"],
        (data.moagem || []).map(r => [getTurnoDesc(r.turnoId), r.inicio, r.termino, r.duracao, r.observacao, getDomainValue(r.umDomainId) || "-", r.motivo, r.createdAt])
      );

      const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
${relatosSheet}
${producaoSheet}
${manutencaoSheet}
${moagemSheet}
</Workbook>`;

      const blob = new Blob([xml], { type: "application/vnd.ms-excel;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio_turno_${this.excelStartDate}_${this.excelEndDate}.xls`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // ── Calendário duplo ──────────────────────────────────────────────────────

    formatDateBR(iso) {
      if (!iso) return "";
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    },

    isoFromYMD(y, m, d) {
      return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    },

    calMonthLabel(year, month) {
      const months = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
      return `${months[month]} ${year}`;
    },

    calCells(year, month) {
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < firstDay; i++) cells.push({ day: null });
      for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, year, month });
      return cells;
    },

    calDayClass(cell) {
      if (!cell.day) return "cal-day-empty";
      const iso = this.isoFromYMD(cell.year, cell.month, cell.day);
      const today = todayISO();
      const isToday = iso === today;
      const isStart = iso === this.excelStartDate;
      const isEnd = iso === this.excelEndDate;
      const inRange = iso > this.excelStartDate && iso < this.excelEndDate;
      return {
        "cal-today": isToday,
        "cal-selected-start": isStart,
        "cal-selected-end": isEnd,
        "cal-in-range": inRange,
        "cal-day-active": cell.day !== null,
      };
    },

    selectCalDay(cell) {
      const iso = this.isoFromYMD(cell.year, cell.month, cell.day);
      if (this.calSelectingStart) {
        this.excelStartDate = iso;
        this.excelEndDate = iso;
        this.calSelectingStart = false;
      } else {
        if (iso < this.excelStartDate) {
          this.excelEndDate = this.excelStartDate;
          this.excelStartDate = iso;
        } else {
          this.excelEndDate = iso;
        }
        this.calSelectingStart = true;
      }
    },

    prevMonth() {
      if (this.calMonth === 0) {
        this.calMonth = 11;
        this.calYear -= 1;
      } else {
        this.calMonth -= 1;
      }
    },

    nextMonth() {
      if (this.calMonth === 11) {
        this.calMonth = 0;
        this.calYear += 1;
      } else {
        this.calMonth += 1;
      }
    },

    prevMonth1() { this.prevMonth(); },
    nextMonth1() { this.nextMonth(); },

    isShortcutActive(days) {
      const today = new Date();
      const endISO = todayISO();
      if (days === 0) return this.excelStartDate === endISO && this.excelEndDate === endISO;
      if (days === "month") {
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        return this.excelStartDate === d.toISOString().split("T")[0] && this.excelEndDate === endISO;
      }
      if (days === "lastMonth") {
        const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return this.excelStartDate === d.toISOString().split("T")[0] && this.excelEndDate === end.toISOString().split("T")[0];
      }
      const d = new Date();
      d.setDate(d.getDate() - days);
      return this.excelStartDate === d.toISOString().split("T")[0] && this.excelEndDate === endISO;
    },
  },
};
</script>

<style scoped>
/* ── Layout ──────────────────────────────────────────────────────────────── */
.shift-report-unified {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 13px;
  padding: 12px;
}

/* ── Card ────────────────────────────────────────────────────────────────── */
.sru-card {
  border-radius: 4px;
  box-shadow: 1px 1px 4px 1px #00000050;
  padding: 8px;
  margin-bottom: 16px;
  max-height: 40vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sru-card-title {
  font-size: 1.5rem;
  padding: 0.3rem 0.2rem;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.sru-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0 12px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 12px;
}

.sru-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sru-date-label {
  font-weight: 600;
  margin: 0;
}

.sru-date-input {
  padding: 4px 8px;
  border: 1px solid #ff6600;
  border-radius: 4px;
  font-size: 13px;
}

.sru-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sru-btn-export {
  display: flex;
  align-items: center;
  column-gap: 0.3rem;
  padding: 0.3rem 0.5rem;
  border-radius: 0.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  background-color: #ffffff;
  color: #ff5f00;
  border: 1px solid #ff5f00;
  font-size: 0.9rem;
}

.sru-btn-export:hover:not(:disabled) {
  background-color: #ff5f00;
  color: #ffffff;
}

.sru-btn-export:active:not(:disabled) {
  transform: translateY(1px);
}

.sru-btn-export:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sru-btn-save {
  padding: 0.3rem 0.8rem;
  border: 1px solid #ff6600;
  background: #fff;
  color: #ff6600;
  transition: background 0.15s, color 0.15s;
  font-weight: 600;
  border-radius: 0.2rem;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
}

.sru-btn-save:hover:not(:disabled) {
  background: #ff6600;
  color: #fff;
}

.sru-btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Tabs ────────────────────────────────────────────────────────────────── */
.sru-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.sru-tab-list {
  display: flex;
  border-bottom: 1px solid #ddd;
  margin-bottom: 0;
}

.sru-tab-item {
  padding: 8px 18px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-bottom: none;
  cursor: pointer;
  font-size: 13px;
  margin-right: 2px;
  border-radius: 4px 4px 0 0;
  user-select: none;
}

.sru-tab-item.active {
  background: #fff;
  border-top: 3px solid #ff6600;
  font-weight: 600;
}

.sru-tab-content {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ── Table ───────────────────────────────────────────────────────────────── */
.sru-table-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.sru-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #343a40;
}

.sru-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
}

.sru-table th {
  background: #343a40;
  color: #fff;
  padding: 6px 8px;
  text-align: left;
  font-size: 12px;
  white-space: nowrap;
}

.sru-table td {
  padding: 4px 6px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}

.sru-table tr:hover td {
  background: #fafafa;
}

.late-insertion td {
  background: #fff3e0 !important;
}

.row-readonly {
  opacity: 0.7;
}

/* ── Inputs ──────────────────────────────────────────────────────────────── */
.sru-input {
  width: 100%;
  border: 1px solid #ff6600;
  border-radius: 3px;
  padding: 3px 6px;
  font-size: 12px;
  box-sizing: border-box;
  transition: box-shadow 0.15s;
}

.sru-input:focus {
  outline: none;
  border-color: #ff6600;
  box-shadow: 0 0 0 3px rgba(255, 102, 0, 0.15);
}

.sru-input:disabled {
  background: #f5f5f5;
  color: #999;
}

.sru-input-sm {
  width: 80px;
}

.sru-input-num {
  width: 100%;
  text-align: right;
}

/* ── Producao table column widths ────────────────────────────────────────── */
.sru-table--producao .sru-col-num   { width: 32px; min-width: 32px; }
.sru-table--producao .sru-col-tipo  { width: 90px; min-width: 80px; }
.sru-table--producao .sru-col-indicador { width: 150px; min-width: 140px; }
.sru-table--producao .sru-col-turno { width: 75px; min-width: 70px; }
.sru-table--producao .sru-col-total { width: 75px; min-width: 70px; }
.sru-table--producao .sru-col-meta  { width: 75px; min-width: 70px; }
.sru-table--producao .sru-col-obs   { width: 130px; min-width: 120px; }
.sru-table--producao .sru-col-ts    { width: 130px; min-width: 120px; }
.sru-table--producao .sru-col-pin   { width: 34px; min-width: 34px; }
.sru-table--producao .sru-col-del   { width: 34px; min-width: 34px; }


/* ── Coluna Área — aba Relatos ───────────────────────────────────────────── */
.sru-table--relatos .sru-col-area { width: 150px; max-width: 150px; overflow: hidden; }

/* ── Scrollbar verde para a aba de Produção ─────────────────────────────── */
.sru-table-wrapper--producao::-webkit-scrollbar        { height: 8px; width: 8px; }
.sru-table-wrapper--producao::-webkit-scrollbar-track  { background: #f0f0f0; }
.sru-table-wrapper--producao::-webkit-scrollbar-thumb  { background: #28a745; border-radius: 4px; }
.sru-table-wrapper--producao::-webkit-scrollbar-thumb:hover { background: #1e7e34; }

/* ── Moagem table — layout fixo com Motivo expansível ───────────────────── */
.sru-table--moagem {
  table-layout: fixed;
  width: 100%;
  min-width: 820px;
}
.moagem-col-num    { width: 32px; }
.moagem-col-turno  { width: 80px; }
.moagem-col-time   { width: 70px; }
.moagem-col-dur    { width: 60px; }
.moagem-col-rot    { width: 80px; }
.moagem-col-um     { width: 80px; }
.moagem-col-motivo { width: 150px; }
.moagem-col-ts     { width: 130px; }
.moagem-col-pin    { width: 34px; }
.moagem-col-del    { width: 34px; }

.sru-table--moagem td, .sru-table--moagem th {
  overflow: hidden;
  white-space: nowrap;
}
.sru-table--moagem .moagem-col-motivo {
  white-space: normal;
}

/* ── Scrollbar verde para a aba de Moagem ───────────────────────────────── */
.sru-table-wrapper--moagem::-webkit-scrollbar        { height: 8px; width: 8px; }
.sru-table-wrapper--moagem::-webkit-scrollbar-track  { background: #f0f0f0; }
.sru-table-wrapper--moagem::-webkit-scrollbar-thumb  { background: #28a745; border-radius: 4px; }
.sru-table-wrapper--moagem::-webkit-scrollbar-thumb:hover { background: #1e7e34; }

/* ── Pin button ──────────────────────────────────────────────────────────── */
.btn-pin {
  background: transparent;
  border: 1px solid #aaa;
  border-radius: 3px;
  padding: 2px 4px;
  cursor: pointer;
  font-size: 13px;
  opacity: 0.4;
  transition: opacity 0.15s, border-color 0.15s;
}
.btn-pin:hover:not(:disabled) {
  opacity: 1;
  border-color: #28a745;
}
.btn-pin--active {
  opacity: 1;
  border-color: #28a745;
  background: #e8f5e9;
}
.btn-pin:disabled {
  cursor: not-allowed;
  opacity: 0.2;
}

.sru-select {
  width: 100%;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid #ff6600;
  border-radius: 3px;
  padding: 3px 4px;
  font-size: 12px;
  background: #fff;
  min-width: 90px;
  transition: box-shadow 0.15s;
}

.sru-select:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 102, 0, 0.15);
}

.sru-select:disabled {
  background: #f5f5f5;
  color: #999;
}

.sru-timestamp {
  font-size: 11px;
  color: #888;
  background: #f9f9f9;
  width: 130px;
}

.sru-duracao {
  color: #ff6600;
  font-weight: bold;
  text-align: center;
  width: 60px;
}

/* ── Manage button ───────────────────────────────────────────────────────── */
.btn-manage {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 2px;
  vertical-align: middle;
  display: inline-flex;
  align-items: center;
}

.btn-manage:hover svg {
  fill: #ff6600 !important;
}

/* ── Add row / delete button ─────────────────────────────────────────────── */
.btn-add-row {
  margin-top: 8px;
  padding: 4px 12px;
  border: 1px solid #4caf50;
  background: #fff;
  color: #4caf50;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.btn-add-row:hover {
  background: #e8f5e9;
}

.btn-add-row:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sru-paste-hint {
  margin: 4px 0 0;
  font-size: 0.75rem;
  color: #999;
}
.sru-linha-num {
  width: 32px;
  min-width: 32px;
  text-align: center;
  color: #999;
  font-size: 0.8rem;
  user-select: none;
}

.btn-delete {
  background: transparent;
  border: 1px solid #c00;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  padding: 1px 5px;
  color: #c00;
  transition: background 0.15s, color 0.15s;
}
.btn-delete:hover:not(:disabled) {
  background: #c00;
  color: #fff;
}

.btn-delete:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* ── Time cell ───────────────────────────────────────────────────────────── */
.time-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sru-time {
  width: 80px;
}

.sru-time::-webkit-calendar-picker-indicator {
  display: none;
}

.icon-clock {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.icon-clock:hover svg {
  fill: #ff6600 !important;
}

/* ── Domain modal ────────────────────────────────────────────────────────── */
.domain-search-row {
  margin-bottom: 8px;
}

.domain-scroll-area {
  max-height: 340px;
  overflow-y: auto;
  margin-bottom: 8px;
  padding-right: 4px;
}

.domain-list {
  margin-bottom: 8px;
}

.domain-list-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.domain-input {
  flex: 1;
}

.domain-add-row {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}

.equip-list-header {
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: 6px;
  padding: 0 0 4px 0;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
}

.equip-list-row {
  display: grid !important;
  grid-template-columns: 1fr 1fr 32px;
  gap: 6px;
  align-items: center;
}

.equip-add-row {
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: 6px;
  margin-bottom: 12px;
  align-items: center;
}

.btn-delete-domain {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  flex-shrink: 0;
  color: #e53935;
}
.btn-delete-domain:hover { color: #b71c1c; }

.btn-add-domain {
  padding: 4px 12px;
  background: #4caf50;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
}

.domain-footer {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.campos-vazios-footer {
  display: flex;
  justify-content: space-between;
}

.campos-vazios-intro {
  margin-bottom: 12px;
  font-size: 0.9rem;
}

.campos-vazios-scroll {
  max-height: 320px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.campos-vazios-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}

.campos-vazios-table th,
.campos-vazios-table td {
  border: 1px solid #e0e0e0;
  padding: 5px 10px;
  text-align: left;
}

.campos-vazios-table thead th {
  background: #f5f5f5;
  font-weight: 600;
}

.btn-save-domain {
  height: 33px;
  padding: 6px 16px;
  background: #ff6600;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.btn-save-domain:hover:not(:disabled) {
  background: #cc5200;
}
.btn-save-domain:active:not(:disabled) {
  background: #993d00;
}

.btn-cancel-domain {
  height: 33px;
  padding: 6px 16px;
  background: #fff;
  color: #666;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.btn-cancel-domain:hover:not(:disabled) {
  background: #f0f0f0;
  border-color: #999;
  color: #333;
}
.btn-cancel-domain:active:not(:disabled) {
  background: #e0e0e0;
  border-color: #777;
  color: #111;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.25s ease, opacity 0.25s ease;
  max-height: 600px;
  overflow: hidden;
}

.accordion-enter,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.group-header {
  background: #eee;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 12px;
  color: #666;
  margin-bottom: 5px;
  border-radius: 4px;
  text-transform: uppercase;
  user-select: none;
}

.group-header:hover {
  background: #e0e0e0;
}

.domain-presets-section {
  margin-bottom: 8px;
}

.domain-presets-header {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.domain-list-item--preset .sru-input:disabled {
  background: #f5f5f5;
  color: #999;
  cursor: default;
}

.domain-divider {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 8px 0;
}

/* ── Excel modal ─────────────────────────────────────────────────────────── */

/* ID-13: Input de busca laranja */
.excel-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.excel-search-inner {
  display: flex;
  align-items: center;
  flex: 1;
  border: 2px solid #ff6600;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
}

.excel-icon-calendar {
  width: 20px;
  height: 20px;
  fill: #ff6600;
  flex-shrink: 0;
  margin-right: 8px;
}

.excel-search-input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  color: #333;
  background: transparent;
  cursor: default;
}

.excel-icon-search {
  width: 24px;
  height: 24px;
  fill: #666;
  cursor: pointer;
  flex-shrink: 0;
}

.excel-icon-search:hover {
  fill: #ff6600;
}

.excel-modal-body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

/* ID-12: Atalhos */
.excel-shortcuts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;
  border-right: 1px solid #eee;
  padding-right: 16px;
}

.excel-shortcuts-title {
  font-weight: 700;
  margin: 0 0 6px;
  font-size: 12px;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
}

.excel-shortcuts a {
  color: #555;
  font-size: 13px;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.15s;
}

.excel-shortcuts a:hover {
  background: #fff3e0;
  color: #ff6600;
}

.excel-shortcuts a.active {
  background: #ff6600;
  color: #fff;
  font-weight: 600;
}

/* ID-11: Calendários duplos */
.excel-calendars {
  display: flex;
  gap: 24px;
  flex: 1;
}

.excel-calendar {
  flex: 1;
  min-width: 200px;
}

.cal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
}

.cal-nav {
  background: none;
  border: 1px solid #ddd;
  border-radius: 3px;
  cursor: pointer;
  padding: 2px 8px;
  font-size: 16px;
  line-height: 1;
  color: #555;
}

.cal-nav:hover {
  background: #f5f5f5;
  color: #ff6600;
  border-color: #ff6600;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.cal-dow {
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  padding: 4px 0;
}

.cal-day {
  text-align: center;
  padding: 5px 2px;
  font-size: 12px;
  border-radius: 3px;
  cursor: default;
  min-height: 26px;
}

.cal-day-active {
  cursor: pointer;
}

.cal-day-active:hover {
  background: #fff3e0;
  color: #ff6600;
}

.cal-day-empty {
  background: transparent;
}

.cal-today {
  border: 1px solid #ff6600;
  font-weight: 700;
  color: #ff6600;
}

.cal-selected-start,
.cal-selected-end {
  background: #ff6600 !important;
  color: #fff !important;
  font-weight: 700;
  border-radius: 3px;
}

.cal-in-range {
  background: #fff3e0;
  color: #333;
}

/* Footer do modal */
.excel-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}

.excel-period-label {
  font-size: 13px;
  color: #555;
  font-weight: 500;
}

.sru-btn-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Divider ─────────────────────────────────────────────────────────────── */
.sru-divider {
  margin: 20px 0;
  border-color: #ddd;
}

/* ── Print ───────────────────────────────────────────────────────────────── */
.print-only {
  display: none;
}

.print-tab-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
}

@media (max-height: 800px) {
  .sru-card {
    max-height: 60vh;
  }
}

@media print {
  .no-print,
  .sru-header,
  .sru-tab-list,
  .sru-fechamento,
  .sru-divider,
  .btn-manage,
  .btn-add-row,
  .btn-delete {
    display: none !important;
  }

  .print-only {
    display: block !important;
  }

  .print-main-title {
    margin-bottom: 16px;
  }

  /* Força todas as 4 abas a aparecerem no PDF, independente da aba ativa */
  .sru-tab-content {
    display: block !important;
    margin-bottom: 24px;
    page-break-inside: avoid;
    page-break-after: auto;
  }

  /* Garante que o container das abas não oculta nada */
  .sru-tabs {
    display: block !important;
  }

  .sru-table-wrapper {
    overflow: visible !important;
  }

  .sru-table {
    width: 100% !important;
    min-width: unset !important;
    page-break-inside: avoid;
  }

  .sru-table th {
    background: #343a40 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .late-insertion td {
    background: #fff3e0 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .sru-input, .sru-select {
    border: none !important;
    padding: 2px !important;
    font-size: 11px !important;
  }
}
</style>
